import { ChartDataset, ChartOptions, TooltipItem } from 'chart.js';
import { JSX, useEffect, useMemo, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaTrophy } from 'react-icons/fa';
import { FaFlagCheckered } from 'react-icons/fa6';
import { HiTrendingUp } from 'react-icons/hi';
import { TbMathAvg } from 'react-icons/tb';
import { Habit } from '../dto/habit';
import { HabitFrequencyType, HabitTargetType, TargetDirection, TargetEvaluation } from '../dto/habit.enum';
import { getTodaysRecord, Record } from '../dto/record';
import { useHabit } from '../hooks/habit.hook';
import { RecordMap } from '../hooks/record.hook';
import {
  avg,
  daysAfter,
  daysBefore,
  daysBetween,
  getCutoffDate,
  getDay,
  getLocaleDate,
  getLocaleShortDate,
  getThemeColor,
  monthDaysBetween,
  monthsBetween,
  round,
  specialDaysBetween,
  TimeFrame,
  weekDaysBetween,
  weeksBetween,
} from '../util/util';
import { ConfirmAction } from './confirm-action';
import { PageLoading } from './page-loading';
import { TimeCount } from './time-count';

interface HabitStats {
  habit: Habit;
  hasRecords: boolean;

  // time frame filtered
  records: Record[];
  startDate: Date;
  endDate: Date;
  dayCount: number;
}

interface AxisConfig {
  min: number;
  max?: number;
}

interface StatisticProps {
  habits: Habit[];
  records: RecordMap;
  activeFooter?: JSX.Element | false;
}

export function Statistic({ habits, records, activeFooter }: StatisticProps): JSX.Element {
  const { t } = useTranslation();
  const { deleteHabit } = useHabit();
  const [timeFrame, setTimeFrame] = useState<TimeFrame>(TimeFrame.MONTH);
  const [habitToDelete, setHabitToDelete] = useState<Habit>();
  const [isDeleting, setIsDeleting] = useState(false);

  function handleDelete() {
    if (!habitToDelete) return;

    setIsDeleting(true);
    deleteHabit(habitToDelete)
      .then(() => setHabitToDelete(undefined))
      .finally(() => setIsDeleting(false));
  }

  const hasRecords = habits.length > 0 && Object.values(records).length > 0;

  const statistics = useMemo(() => {
    if (!hasRecords) return [];

    const cutoffDate = getDay(getCutoffDate(timeFrame));

    const stats = habits.map((h) => {
      const habitRecords = records[h.id];
      const hasRecords = !!habitRecords && habitRecords.length > 0;
      const currentRecord = getTodaysRecord(habitRecords);

      const startDate = h.start > cutoffDate ? h.start : cutoffDate;
      const endDate = h.end ?? daysAfter(currentRecord ? 1 : 0, getDay(new Date()));
      const dayCount = Math.round(daysBetween(startDate, endDate));

      const filteredRecords = habitRecords?.filter((r) => r.date >= startDate) ?? [];

      return {
        habit: h,
        startDate,
        endDate,
        dayCount,
        records: filteredRecords,
        hasRecords,
      };
    });

    const activeHabits = stats.filter((s) => !s.habit.end);
    const endedHabits = stats.filter((s1) => !activeHabits.some((s2) => s2.habit.id === s1.habit.id));

    return [
      { title: 'Active Habits', items: activeHabits },
      { title: 'Ended Habits', items: endedHabits },
    ];
  }, [habits, records, hasRecords, timeFrame]);

  const hasMultipleCategories = statistics.filter((i) => i.items.length > 0).length > 1;

  const hasOldRecords = Object.values(records).some(
    (habitRecords) => habitRecords && habitRecords[0]?.createdAt < daysBefore(10),
  );

  if (habitToDelete) {
    return (
      <ConfirmAction
        title={t('Are you sure you want to {{action}} your habit "{{habit}}"?', {
          action: t('Delete').toLowerCase(),
          habit: t(habitToDelete.title),
        })}
        message={t('This deletes the habit and all associated statistical data. This operation is irreversible!')}
        onConfirm={handleDelete}
        onCancel={() => setHabitToDelete(undefined)}
        loading={isDeleting}
      />
    );
  }

  return hasRecords ? (
    statistics.length ? (
      <div className="flex flex-col gap-10">
        {statistics
          .filter((s) => s.items.length)
          .map((s) => (
            <div key={s.title} className="flex flex-col gap-3">
              {hasMultipleCategories && <h2 className="text-xl text-center mb-1">{t(s.title)}</h2>}
              <StatisticTable
                statistics={s.items}
                timeFrame={timeFrame}
                setTimeFrame={setTimeFrame}
                showTimeFrameTabs={hasOldRecords}
                isEnded={s.title === 'Ended Habits'}
                onDelete={setHabitToDelete}
              />
              {s.title === 'Active Habits' && activeFooter}
            </div>
          ))}
      </div>
    ) : (
      <PageLoading />
    )
  ) : (
    <div className="text-center text-base-content/70">{t('Start tracking a habit to view your statistics!')}</div>
  );
}

interface StatisticTableProps {
  statistics: HabitStats[];
  timeFrame: TimeFrame;
  setTimeFrame: (tf: TimeFrame) => void;
  showTimeFrameTabs: boolean;
  isEnded: boolean;
  onDelete: (habit: Habit) => void;
}

interface ChartData {
  values: ChartValue[];
  config?: AxisConfig;
}

function StatisticTable({
  statistics,
  timeFrame,
  setTimeFrame,
  showTimeFrameTabs,
  isEnded,
  onDelete,
}: StatisticTableProps): JSX.Element {
  const { t } = useTranslation();

  const [openCollapse, setOpenCollapse] = useState<string>();
  const [chartCache, setChartCache] = useState<Map<string, ChartData>>(new Map());

  useEffect(() => {
    // clear cache on change
    setChartCache(new Map());
  }, [statistics]);

  function toggleCollapse(habitId: string) {
    setOpenCollapse(openCollapse === habitId ? undefined : habitId);
  }

  useEffect(() => {
    if (!openCollapse) return;

    const habitStats = statistics.find((s) => s.habit.id === openCollapse);
    if (!habitStats || !habitStats.hasRecords) return;

    // let the layout render first
    setTimeout(() => {
      const { habit, records, startDate, dayCount } = habitStats;

      // chart values
      const values: ChartValue[] = records.map((r) => ({
        date: r.date,
        value: r.achievement,
        target: habit.target.type === HabitTargetType.QUANTITY ? r.target : undefined,
      }));

      // chart empty values
      if (values.length > 0) {
        const firstEntry = values.at(0);
        const lastEntry = values.at(-1);

        const existingDates = new Set(values.map((v) => v.date.toDateString()));
        const missingEntries: ChartValue[] = [];

        for (let i = 0; i < dayCount; i++) {
          const date = daysAfter(i, startDate);

          if (!existingDates.has(date.toDateString())) {
            const target =
              firstEntry && date < firstEntry.date
                ? firstEntry.target
                : lastEntry && date > lastEntry.date
                  ? lastEntry.target
                  : undefined;
            missingEntries.push(
              habit.target.type === HabitTargetType.BOOLEAN ? { date, value: 0, target: 1 } : { date, target },
            );
          }
        }

        if (missingEntries.length > 0) {
          values.push(...missingEntries);
          values.sort((a, b) => a.date.getTime() - b.date.getTime());
        }
      }

      // chart config
      const config =
        habit.target.type === HabitTargetType.RATING
          ? { min: 1, max: 10 }
          : habit.target.type === HabitTargetType.QUANTITY
            ? {
                min: Math.min(0, ...records.map((r) => r.achievement)),
              }
            : undefined;

      setChartCache((prev) => new Map(prev).set(habit.id, { values, config }));
    }, 0);
  }, [openCollapse, statistics]);

  return (
    <div className="join join-vertical w-full">
      {statistics.map(({ habit, hasRecords, endDate, startDate, dayCount, records }, i) => {
        const chartData = chartCache.get(habit.id);
        const chartValues = chartData?.values;
        const chartConfig = chartData?.config;

        return (
          <div
            key={habit.id}
            className={`collapse collapse-arrow join-item border-base-300 border bg-base-100 ${
              i === 0 && 'rounded-t-lg'
            } ${openCollapse === habit.id && 'collapse-open'}`}
          >
            <div className="collapse-title font-semibold" onClick={() => toggleCollapse(habit.id)}>
              {t(habit.title)}
            </div>

            <div className="collapse-content flex flex-col gap-2 overflow-hidden">
              {habit.notes && <div className="text-base-content/70 whitespace-pre-wrap">{t(habit.notes)}</div>}

              {!hasRecords ? (
                <div className="text-center text-base-content/70 italic">{t('No records yet')}</div>
              ) : (
                <>
                  {showTimeFrameTabs && (
                    <div role="tablist" className="tabs tabs-lift tabs-sm">
                      {Object.values(TimeFrame).map((tf) => (
                        <a
                          key={tf}
                          role="tab"
                          className={`flex-grow tab ${timeFrame === tf ? 'tab-active' : ''}`}
                          onClick={() => setTimeFrame(tf)}
                        >
                          {t(tf)}
                        </a>
                      ))}
                    </div>
                  )}

                  {records.length === 0 ? (
                    <div className="text-center text-base-content/70 italic">{t('No records in this timeframe')}</div>
                  ) : !chartValues ? (
                    <PageLoading />
                  ) : (
                    <>
                      {habit.target.type === HabitTargetType.BOOLEAN ? (
                        <BarChart values={chartValues} />
                      ) : (
                        <LineChart values={chartValues} {...chartConfig} />
                      )}

                      <div className="flex items-center justify-center gap-1">
                        <div>
                          <ResultTable
                            habit={habit}
                            records={records}
                            startDate={startDate}
                            endDate={endDate}
                            dayCount={dayCount}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {isEnded && (
                <div className="mt-4 pt-4 border-t border-base-300">
                  <button className="btn btn-sm btn-outline btn-error w-full" onClick={() => onDelete(habit)}>
                    {t('Delete habit')}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface ResultTableProps {
  habit: Habit;
  records: Record[];
  startDate: Date;
  endDate: Date;
  dayCount: number;
}

function ResultTable({ habit, records, startDate, endDate, dayCount }: ResultTableProps) {
  const { t } = useTranslation();

  const { isBoolean, isQuantity, target, outcome, score } = useMemo(() => {
    const isBoolean = habit.target.type === HabitTargetType.BOOLEAN;
    const isQuantity = habit.target.type === HabitTargetType.QUANTITY;

    let target;
    let outcome;

    if (isBoolean) {
      switch (habit.frequency.type) {
        case HabitFrequencyType.DAILY:
          target = dayCount;
          break;

        case HabitFrequencyType.WEEKLY:
          target = round(habit.frequency.count * weeksBetween(startDate, endDate), 1);
          break;

        case HabitFrequencyType.MONTHLY:
          target = round(habit.frequency.count * monthsBetween(startDate, endDate), 1);
          break;

        case HabitFrequencyType.WEEK_DAYS:
          target = weekDaysBetween(habit.frequency.weekDays ?? [], startDate, endDate);
          break;

        case HabitFrequencyType.MONTH_DAYS:
          target = monthDaysBetween(habit.frequency.monthDays ?? [], startDate, endDate);
          break;

        case HabitFrequencyType.SPECIAL_DAYS:
          target = habit.frequency.specialDays
            ? specialDaysBetween(habit.frequency.specialDays, startDate, endDate)
            : 0;
          break;
      }
      outcome = records.filter((r) => r.achievement >= r.target).length;
    } else {
      const averagingRecords = habit.target.evaluation === TargetEvaluation.FINAL ? records.slice(-3) : records;
      target = round(records.length && avg(averagingRecords.map((r) => r.target)), 1);
      outcome = round(records.length && avg(averagingRecords.map((r) => r.achievement)), 1);
    }

    const score =
      habit.target.direction === TargetDirection.REDUCTION
        ? target
          ? Math.round(100 * (target / outcome))
          : -outcome + 100
        : target
          ? Math.round((100 * outcome) / target)
          : outcome + 100;

    return { isBoolean, isQuantity, target, outcome, score };
  }, [habit, records, endDate, startDate, dayCount]);

  return isBoolean || isQuantity ? (
    <table className="table table-xs text-base-content/70">
      <tbody>
        <tr className="!text-base">
          <td>
            <div className="flex flex-wrap gap-1 items-center">
              <FaFlagCheckered />
              {t('Target')}
              <span className="text-base-content/50 text-xs">
                (
                {isBoolean ? (
                  habit.frequency.type === HabitFrequencyType.DAILY ? (
                    t(habit.frequency.type).toLowerCase()
                  ) : (
                    <TimeCount
                      count={habit.frequency.count}
                      frequency={habit.frequency.type}
                      specialDays={habit.frequency.specialDays}
                    />
                  )
                ) : (
                  t('Quantity')
                )}
                )
              </span>
            </div>
          </td>
          <td></td>
          <td>{target}</td>
        </tr>

        <tr className="!text-base">
          <td className="flex gap-1 items-center">
            {isBoolean ? (
              <FaCheckCircle />
            ) : habit.target.evaluation === TargetEvaluation.FINAL ? (
              <HiTrendingUp />
            ) : (
              <TbMathAvg />
            )}
            {t('Outcome')}
          </td>
          <td></td>
          <td>{outcome}</td>
        </tr>
      </tbody>

      <tfoot>
        <tr className="text-base">
          <th className="flex gap-1 items-center font-bold">
            <FaTrophy />
            {habit.target.evaluation === TargetEvaluation.FINAL ? t('Progress') : t('Score')}
          </th>
          <th></th>
          <th>{score === Infinity ? '∞' : `${score}%`}</th>
        </tr>
      </tfoot>
    </table>
  ) : (
    <div className="flex gap-1 items-center">
      <TbMathAvg />
      <span>
        {t('Average')}: {outcome} / {target}
      </span>
    </div>
  );
}

interface ChartValue {
  date: Date;
  value?: number;
  target?: number;
}

interface ChartProps {
  values: ChartValue[];
  min?: number;
  max?: number;
}

function LineChart({ values, min, max }: ChartProps) {
  const { t } = useTranslation();

  const { data, options } = useMemo(() => {
    const hasTargets = values.some((v) => v.target != null);

    const data = {
      labels: values.map((v) => v.date),
      datasets: [
        {
          label: t('Outcome'),
          data: values.map((v) => v.value ?? null),
          fill: false,
          borderColor: getThemeColor('--color-primary'),
          backgroundColor: getThemeColor('--color-primary'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          pointRadius: (context: any) => {
            const label = context.chart.data.labels[context.dataIndex] as Date;
            return !label || [0, 6].includes(label.getDay()) ? 4 : 3;
          },
          tension: 0.4,
          spanGaps: true,
        },
      ].filter((i) => i) as ChartDataset<'line', (number | null)[]>[],
    };

    if (hasTargets)
      data.datasets.push({
        label: t('Target'),
        data: values.map((v) => v.target ?? null),
        fill: false,
        borderColor: getThemeColor('--color-secondary'),
        backgroundColor: getThemeColor('--color-secondary'),
        tension: 0.5,
        pointRadius: values.length > 1 ? 0 : undefined,
        spanGaps: true,
      });

    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: true,
      animation: false,
      plugins: {
        legend: {
          display: hasTargets,
          labels: {
            boxWidth: 12,
            padding: 8,
          },
        },
        tooltip: {
          callbacks: {
            title: (context: TooltipItem<'line'>[]) => getLocaleDate(data.labels[context[0].dataIndex]),
            label: (context: TooltipItem<'line'>) => `${context.raw}`,
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 6,
            callback: (index: string | number) => {
              return getLocaleShortDate(data.labels[+index]);
            },
          },
        },
        y: {
          min: min,
          max: max,
        },
      },
    };

    return { data, options };
  }, [t, values, min, max]);

  return <Line data={data} options={options} />;
}

function BarChart({ values }: ChartProps) {
  const { t } = useTranslation();

  function valueToIcon(value: number | string): string {
    return +value > 0 ? '✔' : +value < 0 ? '✘' : '';
  }

  const { data, options } = useMemo(() => {
    const data = {
      labels: values.map((v) => v.date),
      datasets: [
        {
          data: values.map((v) => v.value),
          fill: false,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          backgroundColor: (context: any) => {
            const label = context.chart.data.labels[context.dataIndex] as Date;
            const value = context.dataset.data[context.dataIndex];

            const color = value > 0 ? getThemeColor('--color-success') : getThemeColor('--color-error');
            return !label || [0, 6].includes(label.getDay()) ? color : color + '90';
          },
          tension: 0.5,
        },
      ],
    };

    const options: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: true,
      animation: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            title: (context: TooltipItem<'bar'>[]) => getLocaleDate(data.labels[context[0].dataIndex]),
            label: (context: TooltipItem<'bar'>) => valueToIcon(context.raw as string | number),
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 6,
            callback: (index: string | number) => {
              return getLocaleShortDate(data.labels[+index]);
            },
          },
        },
        y: {
          min: -1,
          max: 1,
          ticks: {
            stepSize: 1,
            callback: valueToIcon,
          },
        },
      },
    };

    return { data, options };
  }, [t, values]);

  return <Bar data={data} options={options} />;
}
